import React, { useState, useEffect } from 'react';
import { X, Save, Plus, MapPin, Tag, FileText, Image } from 'lucide-react';
import { contactService } from '../../../services/contactService';

export default function CustomerDrawer({ isOpen, onClose, customer, onSave }) {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    industry: 'residential',
    subscriptionStatus: 'inactive',
    annualRevenue: 0,
    communicationPreference: 'email',
    notes: ''
  });
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (customer) {
      setFormData({
        companyName: customer?.companyName || '',
        contactPerson: customer?.contactPerson || '',
        email: customer?.email || '',
        phone: customer?.phone || '',
        industry: customer?.industry || 'residential',
        subscriptionStatus: customer?.subscriptionStatus || 'inactive',
        annualRevenue: customer?.annualRevenue || 0,
        communicationPreference: customer?.communicationPreference || 'email',
        notes: customer?.notes || ''
      });
    } else {
      // Reset form for new customer
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        industry: 'residential',
        subscriptionStatus: 'inactive',
        annualRevenue: 0,
        communicationPreference: 'email',
        notes: ''
      });
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.companyName?.trim() || !formData?.email?.trim()) {
      setError('Company name and email are required');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      if (customer) {
        await contactService?.update(customer?.id, formData);
      } else {
        await contactService?.create(formData);
      }
      onSave?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag?.trim() || !customer) return;

    try {
      await contactService?.addTag(customer?.id, newTag?.trim());
      setNewTag('');
      // Reload customer data
      const updatedCustomer = await contactService?.getById(customer?.id);
      onSave();
    } catch (err) {
      setError(err?.message || 'Failed to add tag');
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await contactService?.removeTag(tagId);
      onSave();
    } catch (err) {
      setError(err?.message || 'Failed to remove tag');
    }
  };

  const handleAddNote = async () => {
    if (!newNote?.trim() || !customer) return;

    try {
      await contactService?.addNote(customer?.id, {
        content: newNote?.trim(),
        noteType: 'general'
      });
      setNewNote('');
      onSave();
    } catch (err) {
      setError(err?.message || 'Failed to add note');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {customer ? 'Customer Details' : 'Add New Customer'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Tabs */}
        {customer && (
          <div className="border-b border-gray-200 px-6">
            <div className="flex gap-4">
              {['details', 'addresses', 'tags', 'notes', 'properties']?.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-3 px-4 font-medium text-sm border-b-2 ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-6">
          {activeTab === 'details' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData?.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Contact Person */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={formData?.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={formData?.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                  <option value="government">Government</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Subscription Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subscription Status
                </label>
                <select
                  value={formData?.subscriptionStatus}
                  onChange={(e) => setFormData({ ...formData, subscriptionStatus: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="trial">Trial</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Annual Revenue */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Revenue
                </label>
                <input
                  type="number"
                  value={formData?.annualRevenue}
                  onChange={(e) => setFormData({ ...formData, annualRevenue: parseFloat(e?.target?.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Communication Preference */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Communication Preference
                </label>
                <select
                  value={formData?.communicationPreference}
                  onChange={(e) => setFormData({ ...formData, communicationPreference: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="sms">SMS</option>
                  <option value="any">Any</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add any additional notes about this customer..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : 'Save Customer'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {activeTab === 'addresses' && customer && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Service Addresses
              </h3>
              {customer?.addresses && customer?.addresses?.length > 0 ? (
                <div className="space-y-3">
                  {customer?.addresses?.map((address) => (
                    <div key={address?.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          {address?.isPrimary && (
                            <span className="inline-block px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded mb-2">
                              Primary
                            </span>
                          )}
                          <p className="font-medium">{address?.street}</p>
                          <p className="text-sm text-gray-600">
                            {address?.city}, {address?.state} {address?.zipCode}
                          </p>
                          {address?.notes && (
                            <p className="text-sm text-gray-500 mt-2">{address?.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No addresses added yet</p>
              )}
            </div>
          )}

          {activeTab === 'tags' && customer && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Customer Tags
              </h3>
              
              {/* Add Tag Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e?.target?.value)}
                  placeholder="Add new tag..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e?.key === 'Enter' && handleAddTag()}
                />
                <button
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Tags List */}
              {customer?.tags && customer?.tags?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {customer?.tags?.map((tag) => (
                    <div
                      key={tag?.id}
                      className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full"
                    >
                      <span className="text-sm">{tag?.tagName}</span>
                      <button
                        onClick={() => handleRemoveTag(tag?.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No tags added yet</p>
              )}
            </div>
          )}

          {activeTab === 'notes' && customer && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Customer Notes
              </h3>

              {/* Add Note Input */}
              <div className="space-y-2">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e?.target?.value)}
                  placeholder="Add a new note..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Note
                </button>
              </div>

              {/* Notes List */}
              {customer?.customerNotes && customer?.customerNotes?.length > 0 ? (
                <div className="space-y-3">
                  {customer?.customerNotes?.map((note) => (
                    <div key={note?.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs text-gray-500">
                          {new Date(note.createdAt)?.toLocaleDateString()} - {note?.createdByName}
                        </span>
                        {note?.isPinned && (
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                            Pinned
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-900">{note?.content}</p>
                      <span className="inline-block mt-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {note?.noteType}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No notes added yet</p>
              )}
            </div>
          )}

          {activeTab === 'properties' && customer && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Image className="w-5 h-5" />
                Property Photos
              </h3>
              {customer?.properties && customer?.properties?.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {customer?.properties?.map((property) => (
                    <div key={property?.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="aspect-video bg-gray-100 flex items-center justify-center">
                        <Image className="w-12 h-12 text-gray-400" />
                      </div>
                      {property?.caption && (
                        <p className="p-2 text-sm text-gray-600">{property?.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No property photos added yet</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}